import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { Alert, Button, FullScreenLoader } from '../../components/ui'
import { ApiError } from '../../api/client'
import { keys } from '../../api/keys'
import { fetchProperty } from '../../api/objects'
import { t } from '../../i18n'
import { PropertyView } from './PropertyView'

/**
 * One property with everything hanging off it, in one request.
 *
 * One request rather than three because the parts are judged together: whether a
 * room may be offered depends on its listing's stored check, and its price only
 * means anything beside the unit it is a price for.
 */
export function PropertyScreen() {
  const { propertyId } = useParams<{ propertyId: string }>()

  const card = useQuery({
    queryKey: keys.property(propertyId ?? ''),
    queryFn: ({ signal }) => fetchProperty(propertyId ?? '', signal),
    enabled: propertyId !== undefined,
  })

  if (propertyId === undefined) return <Alert tone="error">{t('objects.notFound')}</Alert>
  if (card.isPending) return <FullScreenLoader />

  if (card.isError) {
    const notFound = card.error instanceof ApiError && card.error.status === 404
    return (
      <div className="thread__error">
        <Alert tone="error">{notFound ? t('objects.notFound') : t('objects.loadFailed')}</Alert>
        {notFound ? null : (
          <Button
            variant="secondary"
            onClick={() => {
              void card.refetch()
            }}
          >
            {t('common.retry')}
          </Button>
        )}
      </div>
    )
  }

  return <PropertyView card={card.data} />
}
